import { FC, useCallback, ReactNode } from 'react';
import { graphql, useFragment } from 'react-relay';
import { FormattedMessage, defineMessages, useIntl, MessageValue } from 'dibs-react-intl';
import dibsCss from 'dibs-css';
import classnames from 'classnames';
import { Link } from 'dibs-elements/exports/Link';
import { handleLocaleUrl } from 'dibs-intl/exports/urls';
import { useEnhancedBuyerIncentivesClientVariant } from '../../hooks/useEnhancedBuyerIncentivesClientVariant';
import {
    getTradeBuyerProtectionDuration,
    getTradeBuyerProtectionTier,
} from '../../helpers/tradeBuyerProtectionHelper';
import { getBuyerProtectionDuration } from 'dibs-buyer-incentives/exports/getBuyerProtectionDuration';

import { PdpSharedBuyerProtection_user$key } from './__generated__/PdpSharedBuyerProtection_user.graphql';

const userFragment = graphql`
    fragment PdpSharedBuyerProtection_user on User {
        isVerifiedTrade
        tradeFirm @include(if: $isTrade) {
            rewards {
                currentProgram {
                    programInfo {
                        code
                    }
                    childPrograms(filter: "ENHANCED_BUYER_PROTECTION") {
                        code
                    }
                }
            }
        }
    }
`;

const copy = defineMessages({
    readMoreLinkA11y: {
        id: 'PdpSharedBuyerProtection.readMoreLinkA11y',
        defaultMessage: 'Learn more about buyer protection',
    },
});

export const PdpSharedBuyerProtection: FC<{
    user: PdpSharedBuyerProtection_user$key | null;
    showLink?: boolean;
    className?: string;
}> = ({ user: userRef, showLink = false, className }) => {
    const intl = useIntl();
    const user = useFragment(userFragment, userRef);
    const isEnhancedBuyerIncentivesClientVariant = useEnhancedBuyerIncentivesClientVariant();

    const tradeUserRewardsProgram = user?.tradeFirm?.rewards?.currentProgram;
    const tradeProtectionTierCode = tradeUserRewardsProgram?.programInfo?.code;
    const tradeProtectionDurationCode = tradeUserRewardsProgram?.childPrograms?.[0]?.code;
    const showTradeRewardsCopy = !!tradeProtectionDurationCode;

    let readMoreHref = '/about/promise/';

    if (user?.isVerifiedTrade) {
        readMoreHref = '/info/trade/promise/';
    } else if (isEnhancedBuyerIncentivesClientVariant) {
        readMoreHref = '/about/ourpromise/';
    }

    const learnMoreHandler: MessageValue = useCallback(
        (chunks: ReactNode) => (
            <Link
                href={handleLocaleUrl(readMoreHref, intl.locale)}
                ariaLabel={intl.formatMessage(copy.readMoreLinkA11y)}
                target="_blank"
                dataTn="buyer-protection-link"
            >
                {chunks}
            </Link>
        ),
        [readMoreHref, intl]
    );

    return (
        <div className={classnames(dibsCss.sassyFontBodyMedium, className)}>
            <div
                className={classnames(dibsCss.sassyFontBodyMediumHeavy, dibsCss.mbXxsmall)}
                data-tn="buyer-protection-title"
            >
                <FormattedMessage
                    id="PdpSharedBuyerProtection.title"
                    defaultMessage="{days}-Day Buyer Protection"
                    values={{
                        days: getBuyerProtectionDuration(tradeProtectionDurationCode),
                    }}
                />
            </div>
            <div data-tn="buyer-protection-message">
                {showTradeRewardsCopy ? (
                    <FormattedMessage
                        id="PdpSharedBuyerProtection.tradeRewardsMessage"
                        defaultMessage="If your item arrives not as described, we’ll work with you and the seller to make it right. As a {tier}, you get {days} to report any issues you have with the order.{showLink, select, true { <link>Learn More</link>} other {}}"
                        values={{
                            tier: (
                                <span className={dibsCss.sassyFontBodyMediumHeavy}>
                                    {getTradeBuyerProtectionTier(tradeProtectionTierCode)}
                                </span>
                            ),
                            days: (
                                <span className={dibsCss.sassyFontBodyMediumHeavy}>
                                    {getTradeBuyerProtectionDuration(tradeProtectionDurationCode)}
                                </span>
                            ),
                            link: learnMoreHandler,
                            showLink,
                        }}
                    />
                ) : (
                    <FormattedMessage
                        id="PdpSharedBuyerProtection.message"
                        defaultMessage="If your item arrives not as described, let us know within 7 days, and we’ll work with you and the seller to make it right.{showLink, select, true { <link>Learn More</link>} other {}}"
                        values={{ link: learnMoreHandler, showLink }}
                    />
                )}
            </div>
        </div>
    );
};
